/**
 * useRoadmapProgreso — progreso del roadmap de pareja para el mapa público de
 * `/agendar` (docs/plan-ruta-terapeutica-pareja.md §7.5, docs/spec_roadmap_pareja_map.md §5.3).
 *
 * Lee `patient.id` / `patient.rut` desde `useBooking().state` (mismo paciente
 * identificado que ya usa el nudge de Feature 1 — ver BookingWizard.tsx). Sin
 * paciente identificado (RUT no verificado o paciente nuevo) → estado vacío
 * inmediato, sin llamar a la RPC.
 *
 * Semántica (fix 2026-09-28): `get_roadmap_progreso_publico.sesion_actual` es
 * la cantidad de sesiones de pareja COMPLETADAS (o el override del terapeuta),
 * no la estación en la que está el paciente. La estación actual se deriva acá:
 *
 *   estacionActual = completadas + 1   si hay una consulta de pareja agendada
 *                                       (la agenda es lo que desbloquea la
 *                                       siguiente estación)
 *   estacionActual = completadas       si no hay nada agendado (la pareja se
 *                                       queda parada en la última completada)
 *
 * clampeado a [1, total]. Antes el mapa pintaba `completadas` como "actual" →
 * con 11 completadas y la 12 agendada, la 12 seguía con candado.
 *
 * `hasProgressData` — spec §5.3: distingue "paciente con fila en la RPC" de
 * "paciente sin ninguna consulta de pareja" (sin fila → sin botón flotante).
 *
 * `enabled` (FUNC-125): el padre lo prende recién cuando el paciente pasó el
 * gate de contacto de ExistingPatientBooking. Con solo tipear un RUT no se
 * consulta ni se muestra el progreso — si no, un tercero podía ver que ese
 * RUT está en terapia de pareja y en qué sesión va (dato de salud). La fecha
 * de la próxima consulta queda bajo el mismo gate.
 *
 * `refreshKey`: el padre lo incrementa al abrir el mapa y al llegar a la
 * pantalla de éxito — así una consulta recién agendada en el mismo wizard
 * desbloquea la estación sin recargar la página.
 */

import { useEffect, useState } from 'react';
import { useBooking } from '../BookingContext';
import { getProximaConsultaPareja, getRoadmapProgreso } from '../../../services/roadmap.service';
import { TOTAL_SESIONES_PAREJA } from './roadmapSessions';

export interface RoadmapProgresoState {
  /** Sesiones de pareja completadas (u override del terapeuta). */
  completadas: number;
  /** Estación donde está parada la pareja en el mapa (1..total). */
  estacionActual: number;
  total: number;
  /** ISO de la próxima consulta de pareja agendada, o `null`. */
  fechaProximaConsulta: string | null;
  loading: boolean;
  /** `true` sii la RPC devolvió una fila (paciente identificado + ≥1 consulta de pareja u override). */
  hasProgressData: boolean;
  /** Id del paciente — clave del "último progreso visto" que dispara la animación de desbloqueo. */
  patientId: string | null;
}

const EMPTY_STATE: RoadmapProgresoState = {
  completadas: 0,
  estacionActual: 1,
  total: TOTAL_SESIONES_PAREJA,
  fechaProximaConsulta: null,
  loading: false,
  hasProgressData: false,
  patientId: null,
};

export function useRoadmapProgreso(enabled: boolean, refreshKey = 0): RoadmapProgresoState {
  const { state } = useBooking();
  const patient = state.patientVerification?.existingPatient;
  const patientId = patient?.id;
  const patientRut = patient?.rut;

  const [progreso, setProgreso] = useState<RoadmapProgresoState>(EMPTY_STATE);

  useEffect(() => {
    if (!enabled || !patientId || !patientRut) {
      setProgreso(EMPTY_STATE);
      return;
    }

    let cancelled = false;
    setProgreso((prev) => ({ ...prev, loading: true }));

    Promise.all([
      getRoadmapProgreso(patientId, patientRut),
      getProximaConsultaPareja(patientId, patientRut),
    ]).then(([result, fechaProximaConsulta]) => {
      if (cancelled) return;
      if (!result) {
        setProgreso({ ...EMPTY_STATE, loading: false });
        return;
      }
      const total = result.totalSesiones;
      const completadas = Math.min(Math.max(result.sesionActual, 0), total);
      const alcanzada = completadas + (fechaProximaConsulta ? 1 : 0);
      setProgreso({
        completadas,
        estacionActual: Math.min(Math.max(alcanzada, 1), total),
        total,
        fechaProximaConsulta,
        loading: false,
        hasProgressData: true,
        patientId,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, patientId, patientRut, refreshKey]);

  return progreso;
}
